/* eslint-disable max-lines */
import { useTenantDetails } from '@hakimo-ui/hakimo/data-access';
import {
  FaceProfileOverlay,
  SpecialZones,
  SpecialZonesOverlay,
  VideoHighlightBox,
} from '@hakimo-ui/hakimo/ui-elements';
import { HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { PlayIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { useMatchedFaceLayers } from '../../matched-faces/useMatchedFaceLayers';
import {
  useAlarmGroupCamera,
  useAlarmGroupPlayback,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';
import { useAlarmGroupStatic } from '../alarm-provider/contexts/AlarmGroupStaticContext';

interface Props {
  alarmId: string;
  isPlaying: boolean;
  isCurrentAlarm: boolean;
  specialZones?: SpecialZones;
}

export function VideoMedia(props: Props) {
  const { alarmId, isPlaying, isCurrentAlarm, specialZones } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const prevUrlRef = useRef<string | undefined>();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const { seekerTimestamp, isSeekerPaused, toggleSeekerPaused, playbackSpeed } =
    useAlarmGroupTime();
  const { alarmToDetailsMap } = useAlarmGroupCamera();
  const { isBuffering, updateAlarmLoadState } = useAlarmGroupPlayback();

  const { location } = useAlarmGroupStatic();
  const { data: tenantData } = useTenantDetails(location?.tenant_id ?? '');
  const disableMediaDownload = tenantData
    ? tenantData.config?.disableMediaDownload ?? false
    : true;

  const alarmDetails = alarmToDetailsMap[alarmId];

  const {
    media: { video: alarmVideo },
    alarmStartTime,
    cameraName,
  } = alarmDetails;

  const { overlayFaces, boxes: highlightBoxes } = useMatchedFaceLayers({
    matchedFaces: alarmVideo?.matchedFaces,
    cameraName,
    videoStartTime: alarmVideo?.startTime,
    alarmType: alarmDetails.alarm_type,
    baseBoxes: alarmVideo?.boxes,
  });

  useEffect(() => {
    if (!isCurrentAlarm) {
      return;
    }
    const videoElement = videoRef?.current;
    if (videoElement) {
      const newTime = (seekerTimestamp - alarmStartTime) / 1000;
      const diff = Math.abs(videoElement.currentTime - newTime);
      // During playback, only correct when drift exceeds 1s to avoid jitter.
      // When paused, always seek since it's user-initiated.
      if (diff > 1 || !isPlaying) {
        videoElement.currentTime = newTime;
        // After a large seek while playing, the video may be in ended/paused state — restart it.
        if (isPlaying && videoElement.paused) {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          videoElement.play().catch(() => {});
        }
      }
    }
  }, [alarmStartTime, isCurrentAlarm, isPlaying, seekerTimestamp]);

  useEffect(() => {
    if (!videoRef.current || !alarmVideo?.url) {
      return;
    }
    const urlChanged = prevUrlRef.current !== alarmVideo?.url;
    prevUrlRef.current = alarmVideo?.url;
    if (urlChanged) {
      setIsVideoLoaded(false);
      videoRef.current.load();
    }
  }, [alarmVideo?.url]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    const videoElement = videoRef?.current;
    const urlChanged = prevUrlRef.current !== alarmVideo?.url;
    if (isPlaying) {
      videoElement.play().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('video cant be played', {
          videoUrl: alarmVideo?.url,
          error,
        });
        Sentry.captureMessage('Failed to play video', {
          extra: { videoPath: alarmVideo?.url, error },
        });
      });
    } else {
      if (!urlChanged) {
        const isVideoPlaying = !videoElement.paused;
        isVideoPlaying && videoElement.pause();
      }
    }
  }, [alarmVideo?.url, isPlaying]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  const onClickVideo = () => {
    const videoElement = videoRef?.current;
    if (!videoElement || videoElement.currentTime < 0) return;

    toggleSeekerPaused();
  };

  const handleCanPlay = () => {
    setIsVideoLoaded(true);
    updateAlarmLoadState(alarmId, true);
  };

  return (
    <>
      <video
        ref={videoRef}
        className="peer aspect-video h-full w-full rounded-md bg-inherit object-contain"
        muted
        onClick={onClickVideo}
        onCanPlay={handleCanPlay}
        onContextMenu={
          disableMediaDownload ? (e) => e.preventDefault() : undefined
        }
        preload="auto"
      >
        <source src={alarmVideo?.url} type="video/mp4"></source>
      </video>

      {isSeekerPaused && (
        <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
          <div className="transform transition-all duration-300 ease-in-out">
            <PlayIcon className="text-ondark-text-1 h-6 w-6 drop-shadow-lg" />
          </div>
        </div>
      )}

      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center">
          <HakimoSpinner />
        </div>
      )}

      {overlayFaces.length > 0 && (
        <FaceProfileOverlay matchedFaces={overlayFaces} />
      )}

      {!isBuffering && isVideoLoaded && (
        <VideoHighlightBox
          boxes={highlightBoxes}
          videoRef={videoRef}
          videoHeight={videoRef.current?.videoHeight}
          videoWidth={videoRef.current?.videoWidth}
          videoRect={videoRef.current?.getBoundingClientRect()}
        />
      )}
      <SpecialZonesOverlay mediaRef={videoRef} specialZones={specialZones} />
    </>
  );
}
